/**
 * Is the on-screen keyboard up?
 *
 * The bottom navigation has to get out of the way while somebody is typing a
 * guess — on a short phone it and the mode switcher are two bands over an
 * already cramped board, and iOS stacks its own autofill and prediction rows on
 * top of that.
 *
 * The first version asked `window.innerHeight - visualViewport.height > 220`.
 * That leans on innerHeight meaning the same thing everywhere, and on iOS it
 * does not: some versions shrink the layout viewport with the keyboard, some
 * leave it alone, and Safari's own toolbars move the number around again.
 * Reported 2026-08-21 from an iPhone 13 mini where the bar stayed put and
 * covered the board — on a viewport that short there is very little board left.
 *
 * So: compare against the tallest viewport this page has ever had. That is
 * self-calibrating. Whatever innerHeight is doing, a viewport that has shrunk
 * well below its own maximum has something covering it, and on a phone that
 * something is the keyboard.
 *
 * The threshold is 150, not 220: 220 is a third of a 13 mini's viewport, and a
 * keyboard that failed to clear it left the bar exactly where it hurts most.
 */
export const KEYBOARD_SHRINK_PX = 150;

/** Pure: has the viewport shrunk far enough below its own maximum? */
export function isKeyboardOpen(maxHeight, height, threshold = KEYBOARD_SHRINK_PX) {
  if (!maxHeight || !height) return false;
  return maxHeight - height > threshold;
}

/**
 * Watch the visual viewport and report when the keyboard opens or closes.
 *
 * @param {(open: boolean) => void} onChange
 * @param {Window} win  injectable for tests
 * @returns {() => void} cleanup, for a useEffect return
 */
export function watchKeyboard(onChange, win = window) {
  const vv = win.visualViewport;
  if (!vv) return () => {};

  // Seeded from the viewport as it is now, which on a first paint is the full
  // height. It only ever grows, so a page that opens WITH the keyboard already
  // up still calibrates the moment the keyboard goes away.
  let maxHeight = vv.height || 0;

  const onResize = () => {
    const h = vv.height || 0;
    if (h > maxHeight) maxHeight = h;
    onChange(isKeyboardOpen(maxHeight, h));
  };

  vv.addEventListener("resize", onResize);
  return () => vv.removeEventListener("resize", onResize);
}

export default watchKeyboard;
